// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useMemo } from 'react';

import type { LabelTypeId, Label } from '@evolvemep/api-ts';
import { Badge, ColorSwatch, Flex, type FlexProps, Text } from '@mantine/core';

import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { BADGE_GAP, useBadgeTruncation, OVERFLOW_BADGE_WIDTH } from 'components/Mantine/Labels/useBadgeTruncation';
import { isNotNil } from 'helpers/isNotNil';
import { useLabels } from 'hooks/typeCacheHooks/useLabels/useLabels';

type Props = {
  labels: Pick<Label, 'labelId'>[];
  labelTypeId: LabelTypeId;
  fallback?: ReactNode;
  containerProps?: FlexProps;
  /**
   * If true, will truncate labels that don't fit into a "+n" badge
   * @defaultValue true
   */
  truncate?: boolean;
};

export const LabelWithColorSwatch = ({ label }: { label: Label }) => (
  <Flex gap="xs" align="center" style={{ overflow: 'hidden' }}>
    <ColorSwatch size={10} color={label.color} withShadow={false} />
    <Text ta="left" lineClamp={2}>
      {label.name}
    </Text>
  </Flex>
);

export const LabelBadges = ({
  labels: parentLabels,
  labelTypeId,
  containerProps,
  truncate = true,
  fallback = '--',
}: Props) => {
  const { labelMap } = useLabels(labelTypeId);

  const labels = useMemo(
    () =>
      parentLabels
        // Always pull the label info from `labelMap` for consistency.
        .map((l) => labelMap[l.labelId])
        .filter(isNotNil)
        .toSorted((a, b) => a.name.localeCompare(b.name)),
    [parentLabels, labelMap],
  );

  const { containerRef, visibleCount, remainder } = useBadgeTruncation(labels.length, '[data-label-badge]', truncate);

  if (labels.length === 0) return fallback;

  return (
    <AgGridStyleTooltip
      openDelay={250}
      hidden={!truncate}
      label={
        <>
          {labels.map((label) => (
            <LabelWithColorSwatch key={label.labelId} label={label} />
          ))}
        </>
      }
    >
      <Flex
        gap={BADGE_GAP}
        ref={containerRef}
        w="100%"
        miw={0}
        style={{ overflow: 'hidden' }}
        wrap={truncate ? undefined : 'wrap'}
        {...containerProps}
      >
        {labels.map((label, i) => (
          <Badge
            tt="none"
            size="md"
            radius="sm"
            key={label.labelId}
            color={label.color}
            autoContrast
            variant="filled"
            title={label.name}
            style={{
              cursor: 'inherit',
              pointerEvents: 'none',
              flexShrink: 0,
              visibility: i < visibleCount ? 'visible' : 'hidden',
              position: i < visibleCount ? undefined : 'absolute',
            }}
            data-label-badge
          >
            {label.name}
          </Badge>
        ))}
        {remainder > 0 && (
          <Badge
            tt="none"
            size="md"
            variant="filled"
            radius="sm"
            c="black"
            color="gray.0"
            fw={500}
            miw={OVERFLOW_BADGE_WIDTH}
            style={{ cursor: 'inherit', pointerEvents: 'none', flexShrink: 0 }}
          >
            + {remainder}
          </Badge>
        )}
      </Flex>
    </AgGridStyleTooltip>
  );
};
